import { Grid, useMediaQuery } from "@mui/material";
import { animated, useSpring } from "@react-spring/web";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import BroadcastModal from "../../components/BroadcastModal/BroadcastModal";
import Loader from "../../components/CustomLoader";
import DeleteModal from "../../components/DeleteModal";
import SaveChangesModal from "../../components/SaveChangesModal";
import SegmentSetting from "../../components/SegmentBuilder/Main/SegmentSetting";
import Sidepane from "../../components/SegmentBuilder/Main/Sidepane";
import WheelLayout from "../../components/SegmentBuilder/Main/WheelLayout";
import SegmentView from "../../components/SegmentView";
import WheelComponent from "../../components/WheelComponent";
import { initialSegmentNode, newSegment } from "../../constants/wheelData";
import useSegmentSettings from "../../hooks/useSegmentSetting";
import { usePaletteColor } from "../../hooks/usePaletteColor";
import { getUserBgWallpapersAction } from "../../redux/actions/wallpapers";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { updateWallpaperInitialState } from "../../redux/actions/wheelWallpaper";
import {
  addWheelData,
  handleCloseMenu,
  handleMenuChange,
  pushToHistoryAction,
  resetBuilder,
  setBusinessBoxWheel,
  setDesignTabTarget,
  setIsDraft,
  setIsSavingWheel,
  setSelectedSegmentCharCount,
  setWheelId,
  setWheelRotateBoxVisibility,
  setWheelSegments,
  updateSegmentSuggestedColors,
  markChangesSaved,
  setWheelPreview,
} from "../../redux/reducers/segmentBuilder";
import {
  getHasUnsavedChanges,
  getRotateBoxVisibility,
  getSelectedMenu,
  getSelectedSegment,
  getSelectedSegmentCharCount,
  getWheelAuthorId,
  getWheelAutoResizing,
  getWheelBusinessBox,
  getWheelId,
  getWheelIsDraftValue,
  getWheelIsLiveValue,
  getWheelName,
  getWheelSegments,
} from "../../redux/selectors/segmentBuilderSelectors";
import { getWheelDetails, takeWheelScreenshot } from "../../services/api";
import {
  useBroadcastChanges,
  useCreateWheelV2,
  useGetSuggestedColorPalette,
  useUpdateWheelV2,
} from "../../services/mutations";
import {
  convertSnakeToCamel,
  extractItemsFromSegments,
  fetchImageAsBase64,
  getHexColors,
  getLoggedInUser,
} from "../../utils/helpers";
import Editor from "./Editor";
import CaptureSegmentDropProvider from "./CaptureSegmentDropProvider";
import BuilderCaptureDropZone from "./BuilderCaptureDropZone";
import BuilderCapturedOnWheel from "./BuilderCapturedOnWheel";
import { CAPTURE_ENABLED } from "../../constants/featureFlags";
import RightSection from "./RightSection";
import SegmentActions from "./SegmentActions";
import * as Styled from "./style";
import "./styles.css";
import WaitlistFormModal from "../../components/WaitlistModal";
import { setWheelSettingsTab } from "../../redux/reducers/wheelSettings";
import { ASSETS } from "../../utils/config";
import UpdateLiveClones from "../../components/BroadcastModal/UpdateLiveClones";
import { setColorPaletteDefault } from "../../redux/reducers/colorPalette";
import BusinessBoxBuilder from "../../components/SegmentBuilder/Main/BusinessBoxBuilder";
import GallerySettings from "../../components/WheelGallery/GallerySettings";
import { isEmpty, isEqual } from "lodash";
import { revertGlobalSettingsFromApiFormat } from "../../utils/segmentBuilderUtils";
import HotbarBuilder from "../../components/SegmentBuilder/Main/HotbarBuilder";
import { setSegmentItems } from "../../redux/reducers/newhotbar";
import PreviewCarousel from "../../components/NewHotbar/PreviewCarousel";
import ConfirmationModal from "../../components/SegmentBuilder/Main/BusinessBoxBuilder/children/ConfirmationModal";
import { redistributeProportionally } from "../../components/SegmentBuilder/Main/SegmentsTab";
import { countAllNodes } from "../../components/SegmentBuilder/Main/SegmentsTab/SegmentsTabHelpers";
import { extractMediaUrlsOnly } from "../../utils/segmentUtils";
import { sendWheelMedia } from "../../services/api";
import MoveBlockModal from "../../components/SegmentBuilder/Shared/MoveBlockModal";
// Screenshot functionality moved to backend Lambda
import { closeModal } from "../../redux/reducers/dialog";
import { countTextCharacters } from "../../components/SegmentBuilder/Main/SegmentsTab/SegmentsTabHelpers";
import { charLimitInASegment } from "../../components/SegmentSettings";
import SegmentLibrary from "../../components/SegmentBuilder/Main/SegmentLibrary";
import MobileModal from "../../components/Gallery/MobileModal";
import useScreenSize from "../../hooks/useScreenSize";
import { shouldGateBuilder } from "./builderMobileGate";
export default function SegmentBuilder() {
  // fix-capture-stg-builder-390-squeezed-desktop-v3: gate the builder behind
  // the SAME desktop-only interstitial Gallery/View-Wheel already show below
  // their shared 768 mobile floor, instead of rendering the desktop canvas
  // (Wheel-Name/Subtitle slivers, tool-rail overflow) at phone width.
  const { width: viewportWidth } = useScreenSize();
  const isBuilderGated = shouldGateBuilder(viewportWidth);
  const isUltraWide = useMediaQuery("(min-width:2500px)");
  const selectedMenu = useSelector(getSelectedMenu);
  const [isSideMenuActive, setIsSideMenuActive] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  // reproof-stg-add-to-segment-s2-after-5412: holds `{json, seq, ownerSegmentId}`
  // set atomically in `handleNodesChange` below (see `nodesChangeSeqRef`'s
  // doc comment above for why a bare content string isn't enough on its
  // own). `editorState` is derived from it below so every existing reader
  // of the plain json string is unaffected.
  const [nodesChangeEvent, setNodesChangeEvent] = useState(null);
  const editorState = nodesChangeEvent?.json ?? "";
  // stgport-add-to-segment-0911: mirrors `editorState` synchronously, updated
  // inside `handleNodesChange` itself (not via the `setEditorState` state
  // setter, whose new value isn't visible through any closure taken before
  // the next render). `flushCurrentCanvasNow` below reads this ref so it can
  // write the canvas's true CURRENT content into redux without waiting for
  // the `useEffect([editorState])` below to get its own render turn — see
  // that callback's doc comment for why waiting is what drops content.
  const editorStateRef = useRef("");
  // batch-proof-stg-4a40541d: which Segment `editorStateRef`/`editorState`
  // ACTUALLY belongs to, captured synchronously inside `handleNodesChange`
  // via a live store read — NOT the `selectedArc` render closure the guarded
  // effect below used to key off of. A Segment switch remounts the craft
  // `<Frame>` (`components/SegmentView`, `key={selectedArc?.id}`), and that
  // remount's own initial `onNodesChange` fires DURING RENDER (React logs
  // "Cannot update a component while rendering a different component" —
  // `@craftjs/utils`'s nanoid dedupe), which can land the effect's read of
  // `selectedArc` ONE SEGMENT BEHIND the `editorState` it is about to apply:
  // the switch's OWN dispatch has already landed in the store, but this
  // component's render carrying the fresh `selectedArc` hasn't committed yet.
  // MEASURED live (main.4a40541d.js, wheel 127): adding one Capture to TWO
  // Segments where NEITHER was pre-open landed correctly in redux via
  // `flushCurrentCanvasNow` for the FIRST target, then the second target's
  // switch-triggered remount fired this effect with the stale `selectedArc`
  // still pointing at the FIRST target, silently overwriting its
  // just-flushed content with the second (newly-mounted, still-empty)
  // Segment's own initial canvas JSON. `flushCurrentCanvasNow` was already
  // immune (it reads `store.getState()` directly); this ref gives the
  // fallback writer the same immunity.
  const editorStateOwnerSegmentIdRef = useRef(null);
  // Set by `flushCurrentCanvasNow` — lets the generic `useEffect([editorState])`
  // below recognize content it has already correctly applied and skip
  // re-applying it against whatever segment is selected by the time it runs.
  const lastFlushedEditorStateRef = useRef(null);
  // reproof-stg-add-to-segment-s2-after-5412: #5405/#5412 fixed the guarded
  // effect's TARGET id and its ARRAY source, but there is a THIRD staleness
  // this file's refs cannot see: a Segment switch's Frame remount fires
  // `onNodesChange` DURING RENDER with that segment's OWN pre-insert content
  // (the same "Cannot update a component while rendering a different
  // component" hazard named above) — now correctly attributed to the RIGHT
  // segment id (post-#5405) and rebuilt from a live array (post-#5412), but
  // this generic effect has no way to tell that content apart from a real
  // edit. If that phantom `editorState` update's OWN effect happens to run
  // (React schedules it as a passive effect on ITS OWN commit) AFTER a LATER
  // real Capture insert has already correctly flushed that exact segment via
  // `flushCurrentCanvasNow`, it silently reverts the segment back to its
  // pre-insert content — `lastFlushedEditorStateRef` only catches an effect
  // re-processing the SAME content that was already flushed, not a
  // DIFFERENT (older) content arriving late for the same segment. MEASURED
  // live on web-stg.iyuga.net (bundle main.aa52ce72.js, PR#5412 already
  // deployed): 2 independent full matrix runs still reproduced the
  // pre-#5405 byte signature for the "neither multi-select target pre-open"
  // case (first target 863B/marker-absent, second 819B/marker-present) —
  // see reproof-stg-add-to-segment-s2-after-5412.
  //
  // FIX: tag every `onNodesChange` event with a monotonically increasing
  // sequence number (`nodesChangeSeqRef`), captured atomically alongside its
  // json/owner in ONE state update (`nodesChangeEvent`, replacing the bare
  // `editorState` string) so a late-running effect for an OLD event can
  // never be confused with a newer one. `flushCurrentCanvasNow` records, per
  // segment, the highest sequence number it has successfully written
  // (`flushedSeqBySegmentRef`). The guarded effect below refuses to write
  // any event whose sequence number is at or behind that segment's
  // already-flushed sequence — this holds regardless of which effect's
  // passive callback happens to be scheduled or run first, since it compares
  // event identity (a plain integer), not timing.
  const nodesChangeSeqRef = useRef(0);
  const flushedSeqBySegmentRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [isEmptyWheel, setIsEmptyWheel] = useState(false);
  const [isDirty, setIsDirty] = useState(false); // Track unsaved changes
  void isDirty; // set in addSegment; reserved for unsaved-changes UI
  const [waitListModalVisible, setWaitListModalVisible] = useState(false);
  const selectedArc = useSelector(getSelectedSegment);
  const hasUnsavedChanges = useSelector(getHasUnsavedChanges);

  const createWheel = useCreateWheelV2();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const wheelIdInRedux = useSelector(getWheelId);
  const wheelNameInRedux = useSelector(getWheelName);
  const updateWheel = useUpdateWheelV2(wheelIdInRedux ?? id);
  const rotateBoxVisibility = useSelector(getRotateBoxVisibility);

  const [modalOpen, setModalOpen] = useState(false);
  const [onLeave, setOnLeave] = useState(() => {});

  const [gallerySettingsOpen, setGallerySettingsOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const [showUpdateCloneModal, setshowUpdateCloneModal] = useState(false);

  const bboxData = useSelector(getWheelBusinessBox);

  const galleryBusinessBox = useSelector(
    (state) => state.userProfile?.userProfile?.business_box,
  );
  const automaticResizing = useSelector(getWheelAutoResizing);
  const [overLimitWarningShown, setOverLimitWarningShown] = useState(false);
  void overLimitWarningShown;
  void setOverLimitWarningShown;
  const prevTotalRef = useRef(0);

  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);

  const {
    selectedType,
    selectedItem,
    isPreviewOpen,
    selectedSegment,
    segments,
  } = useSelector((state) => state?.hotbar);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  function sortSegmentsByPosition(segments) {
    return segments.sort((a, b) => a.position - b.position);
  }

  // Ref to track if we've shown the toast for this segment
  const hasShownLimitToastRef = useRef(false);
  void hasShownLimitToastRef;

  // useEffect(() => {
  //   if (
  //     selectedSegmentCharCount >= charLimitInASegment &&
  //     !hasShownLimitToastRef.current
  //   ) {
  //     toast.error("Segment text limit has been reached.");
  //     hasShownLimitToastRef.current = true;
  //   } else if (selectedSegmentCharCount < charLimitInASegment) {
  //     // Reset the flag when count goes below the limit again
  //     hasShownLimitToastRef.current = false;
  //   }
  // }, [selectedSegmentCharCount, charLimitInASegment]);

  useEffect(() => {
    dispatch(setColorPaletteDefault({ paletteType: "segment" }));
    if (!selectedArc && selectedMenu === 2) dispatch(handleCloseMenu());
    // Don't auto-switch to SegmentSetting when SegmentLibrary is open
    if (selectedArc && selectedMenu !== 6)
      dispatch(handleMenuChange({ num: 2 }));

    if (!selectedArc?.selected) {
      dispatch(handleCloseMenu());
    }
    return () => {
      dispatch(setColorPaletteDefault({ paletteType: "wheel" }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArc?.id]);

  function mergeEmailsPermission(emails, permission) {
    return [...new Set([...emails, ...permission])];
  }
  void mergeEmailsPermission;

  const navigateToWheel = (id) => {
    navigate(`/wheel/${id}`);
  };

  const [initialWallpaperSettings, setInitialWallpaperSettings] =
    useState(null);
  const [base64Wallpaper, setBase64Wallpaper] = useState(null);
  const [initialSegmentsData, setInitialSegmentsData] = useState(null);
  const [initialCenterMedia, setInitialCenterMedia] = useState(null);
  const wheelInformation = useSelector((state) => state.segmentBuilder);
  const wheelWallpaperSettings = useSelector(
    (state) => state.wheelWallpaperReducer,
  );

  // Helper function to extract only relevant wallpaper properties for comparison
  const getWallpaperComparisonProps = (wallpaperSettings) => {
    if (!wallpaperSettings) return null;

    // Handle both camelCase (Redux) and snake_case (API) formats
    return {
      wallpaperTransparency:
        wallpaperSettings.wallpaperTransparency ??
        wallpaperSettings.wallpaper_transparency,
      wallpaperResizing:
        wallpaperSettings.wallpaperResizing ??
        wallpaperSettings.wallpaper_resizing,
      wallpaperOverlayColor:
        wallpaperSettings.wallpaperOverlayColor ??
        wallpaperSettings.wallpaper_overlay_color,
      bgWallpaper:
        wallpaperSettings.bgWallpaper ?? wallpaperSettings.bg_wallpaper,
      isPattern: wallpaperSettings.isPattern ?? wallpaperSettings.is_pattern,
      segmentBgColor:
        wallpaperSettings.segmentBgColor ?? wallpaperSettings.segment_bg_color,
      currentBg: wallpaperSettings.currentBg ?? wallpaperSettings.current_bg,
      selectedColorType:
        wallpaperSettings.selectedColorType ??
        wallpaperSettings.selected_color_type,
      patternColor:
        wallpaperSettings.patternColor ?? wallpaperSettings.pattern_color,
    };
  };

  const getWheelData = async (id) => {
    try {
      // Fetch wheel details
      setLoading(true);
      const wheelDetails = await getWheelDetails(id);
      if (wheelDetails?.data?.data) {
        let response = wheelDetails?.data?.data;
        console.log(response, "response", "=-=-=-=-=-=-=-=");
        setInitialWallpaperSettings(response?.wallpaper_settings);
        setInitialCenterMedia(response?.wheel_center_media);
        setInitialSegmentsData(response?.wheel_segments);

        const status_code = response?.status_code;
        console.log(status_code, "status_code");

        const is_editable = response?.is_editable;
        // const NotMyWheel =
        //   getLoggedInUser()?.id !== response?.wheel_author?.user_id;
        // const isWheelCloned = response?.is_editable;
        // const isWheelLive = response?.is_live;
        // const isWheelLocked = response?.is_locked;
        const globalSettings = response?.global_settings;
        if (!is_editable) {
          navigateToWheel(id);
          return;
        }

        const modifiedSegmentData = response.wheel_segments.map((s) => {
          const { nodes, position, ...rest } = s;
          const { activity, category, ...settingsRest } = s.settings;

          const parsedNodes =
            typeof nodes === "string" ? JSON.parse(nodes) : nodes;

          return {
            ...rest,
            selected: position === selectedArc?.position ? true : false,
            position,
            category: 1, // This will replace with actual category id
            settings: convertSnakeToCamel(settingsRest),
            nodes: parsedNodes,
            is_modified: false,
            initialNode: parsedNodes, // Store the initial node state
          };
        });

        dispatch(
          updateWallpaperInitialState(
            convertSnakeToCamel(response.wallpaper_settings),
          ),
        );
        dispatch(
          addWheelData({
            ...response,
            globalSettings: revertGlobalSettingsFromApiFormat(globalSettings),
            wheel_segments: sortSegmentsByPosition(modifiedSegmentData),
          }),
        );
        dispatch(handleMenuChange({ num: selectedMenu }));
        if (!isEmpty(response.business_box)) {
          dispatch(setBusinessBoxWheel(response.business_box));
        }
        if (isEmpty(response.business_box) && !isEmpty(galleryBusinessBox)) {
          dispatch(setBusinessBoxWheel(galleryBusinessBox));
        }
        // const newHeights = {};
        // modifiedSegmentData.forEach((seg) => {
        //   newHeights[seg.settings.id] = seg.content_height;
        // });
        // dispatch(setContentHeights(newHeights));
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.response.data.message);
      if (error.response.data.status_code === 400) {
        navigateToWheel(id);
        return;
      }
    }
  };

  useEffect(() => {
    if (id) getWheelData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const wheelData = useSelector(getWheelSegments);
  const authorId = useSelector(getWheelAuthorId);
  const dispatch = useDispatch();
  const store = useStore();
  const nav = useNavigate();
  const categoryData = useSelector((state) => state.categoryColors);
  void categoryData;

  console.log(selectedSegmentCharCount, "yamaW", selectedArc);

  useEffect(() => {
    if (id) {
      const processedSegments = extractItemsFromSegments(wheelData);
      dispatch(setSegmentItems(processedSegments));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelData, id]);

  function arraysAreSame(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, i) => item.value === arr2[i].value);
  }

  //Validation for the resizing the segments
  useEffect(() => {
    const total = wheelData.reduce((sum, sb) => sum + sb.value, 0);

    if (Math.abs(total - 100) > 0.01) {
      const unlockedSegments = wheelData.filter((sb) => !sb.resize_lock);
      const lockedValue = wheelData.reduce(
        (sum, sb) => sum + (sb.resize_lock ? sb.value : 0),
        0,
      );

      const availableValue = 100 - lockedValue;
      const distributedValues = redistributeProportionally(
        availableValue,
        unlockedSegments,
      );

      let index = 0;
      const correctedSegments = wheelData.map((sb) => ({
        ...sb,
        value: sb.resize_lock
          ? sb.value
          : Number(distributedValues[index++].toFixed(2)),
      }));

      console.log(correctedSegments, "CORR 1", prevTotalRef.current);

      if (!arraysAreSame(correctedSegments, wheelData)) {
        dispatch(setWheelSegments(correctedSegments));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelData]);

  const {
    wallpaperTransparency,
    bgWallpaper,
    segmentBgColor,
    currentBg,
    wallpaperHuePercentage,
    wallpaperHueColor = "#ffffff",
  } = useSelector((state) => state.wheelWallpaperReducer);

  // The solid-color wheel wallpaper (currentBg === 0) may be a palette
  // reference token (e.g. "wheel_suggested_color_1") rather than a hex — see
  // SegmentColor's "Wheel Palette" tab. Resolve it to a real color before it
  // reaches CSS, or the browser gets an invalid value and the preview renders
  // white (mirrors pages/Segment + ViewWheel).
  const { getPaletteColor } = usePaletteColor();
  const resolvedBgColor = getPaletteColor(segmentBgColor);

  useEffect(() => {
    const convertWallpaperToBase64 = async () => {
      if (bgWallpaper && bgWallpaper?.startsWith("http")) {
        try {
          const base64 = await fetchImageAsBase64(bgWallpaper);
          setBase64Wallpaper(base64);
        } catch {
          setBase64Wallpaper(bgWallpaper);
        }
      } else {
        setBase64Wallpaper(bgWallpaper);
      }
    };

    convertWallpaperToBase64();
  }, [bgWallpaper]);

  useEffect(() => {
    if (getLoggedInUser()?.id) {
      dispatch(getUserBgWallpapersAction());
    }
    setTimeout(() => {
      if (id) {
        setIsEmptyWheel(false);
      } else if (wheelData?.length === 0) {
        setIsEmptyWheel(true);
      }
    }, 1000);

    return () => {
      dispatch(resetBuilder());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSideMenuActive(!!selectedMenu);
  }, [selectedMenu]);

  const {
    getColor,
    handleChange,
    getMaxPosition,
    handleRotationData,
    generatePayload,
  } = useSegmentSettings() || {};

  const handleCloseSidePane = () => dispatch(handleCloseMenu());

  const [openLibraryInBusinessBox, setOpenLibraryInBusinessBox] =
    useState(false);

  const handleBusinessBoxEditorOpen = (openLibrary = false) => {
    setOpenLibraryInBusinessBox(openLibrary);
    setGallerySettingsOpen(true);
  };

  const handleReplaceBusinessBoxClick = () => {
    handleBusinessBoxEditorOpen(true); // Pass true to open library section
  };

  const handleEditBusinessBoxClick = () => {
    if (
      !isEmpty(galleryBusinessBox) &&
      galleryBusinessBox?.id === bboxData?.id
    ) {
      setConfirmationModalOpen(true);
    } else {
      handleBusinessBoxEditorOpen();
    }
  };

  const componentMap = [
    {
      isVisible: selectedMenu === 1,
      Component: Sidepane,
      props: {
        isVisible: selectedMenu === 1,
        onClose: handleCloseSidePane,
      },
    },
    {
      isVisible: selectedMenu === 2,
      Component: SegmentSetting,
      props: {
        isVisible: selectedMenu === 2,
        onClose: handleCloseSidePane,
      },
    },
    {
      isVisible: selectedMenu === 3,
      Component: RightSection,
      props: {
        segmentSettings: selectedArc?.settings || {},
        handleChange,
        isVisible: selectedMenu === 3,
        onClose: handleCloseSidePane,
      },
    },
    {
      isVisible: selectedMenu === 4,
      Component: HotbarBuilder,
      props: {
        isVisible: selectedMenu === 4,
        onClose: handleCloseSidePane,
      },
    },
    {
      isVisible: selectedMenu === 5,
      Component: BusinessBoxBuilder,
      props: {
        isVisible: selectedMenu === 5,
        onClose: handleCloseSidePane,
        onEditClick: handleEditBusinessBoxClick,
        onReplaceClick: handleReplaceBusinessBoxClick, // Add this
      },
    },
    {
      isVisible: selectedMenu === 6,
      Component: SegmentLibrary,
      props: {
        isVisible: selectedMenu === 6,
        onClose: handleCloseSidePane,
        // onEditClick: handleEditBusinessBoxClick,
        // onReplaceClick: handleReplaceBusinessBoxClick, // Add this
      },
    },
    // GR-3 (ISP-9999) — Capture (menu 8) rendered through the exact same
    // componentMap slot as every other menu, at the same width as its
    // neighbours. Flag-gated: OFF => this entry is never added, selectedMenu
    // can never reach 8 (SideMenu.jsx's rail item is gated identically), so
    // the builder renders byte-identical to today. Ported from sandbox
    // as-is, minus the unrelated WHEEL_COPILOT_ENABLED menu-7 entry (out of
    // Capture's scope).
    ...(CAPTURE_ENABLED
      ? [
          {
            isVisible: selectedMenu === 8,
            Component: BuilderCapturedOnWheel,
            props: {
              isOpen: selectedMenu === 8,
              onClose: handleCloseSidePane,
              wheelId: wheelIdInRedux ?? id,
              wheelName: wheelNameInRedux,
            },
          },
        ]
      : []),
  ];

  const firstDivStyles = useSpring({
    width: !!selectedMenu
      ? selectedMenu === 5
        ? "68vw" // Business Box gets more space on left
        : "64vw"
      : "94vw",
    config: { duration: 500 },
  });

  const secondDivStyles = useSpring({
    width: !!selectedMenu
      ? selectedMenu === 5
        ? "25vw" // Business Box side pane is narrower
        : "30vw"
      : "0vw",
    config: { duration: 500 },
  });

  useEffect(() => {
    if (authorId && getLoggedInUser().id !== authorId) {
      nav(`/wheel/${id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorId]);

  const handleSwitch = () => setShowLabels(!showLabels);

  const [lockWarningModalOpen, setLockWarningModalOpen] = useState(false);

  const addSegment = () => {
    //    This prevents adding new segments when all are locked
    //    or if your largest unlocked segment is too small (<= 1).
    const allLocked =
      wheelData.length > 0 && wheelData.every((sb) => sb.resize_lock);
    const unlockedSegments = wheelData.filter((sb) => !sb.resize_lock);
    unlockedSegments.sort((a, b) => b.value - a.value);

    // If everything is locked or the biggest unlocked segment is ≤ 1, show modal
    if (
      allLocked ||
      (unlockedSegments.length === 1 && unlockedSegments[0].value <= 1) ||
      (unlockedSegments.length > 0 && unlockedSegments[0].value <= 1)
    ) {
      setLockWarningModalOpen(true);
      return;
    }

    // Rest of your existing addSegment function
    setIsDirty(true);
    const newWheelData = wheelData.map((d, i) => ({
      ...d,
      position: typeof d.position === "number" ? d.position : (i + 1) * 10,
    }));

    const id = newWheelData.length + 1;

    const { color, colorInner } = getColor(newWheelData, newWheelData.length);

    const lastPosition = getMaxPosition(newWheelData);

    const currIndex = newWheelData.findIndex((e) => e.selected);
    let currentPos = 10;
    if (currIndex !== -1) {
      const nextIndex = currIndex + 1;
      currentPos =
        nextIndex < newWheelData.length
          ? newWheelData[nextIndex].position
          : lastPosition + 10;
    } else if (newWheelData.length > 0) {
      currentPos = lastPosition + 10;
    }

    const updatedWheelData = newWheelData.map((d, i) => ({
      ...d,
      selected: false,
      position: currIndex > -1 && i > currIndex ? d.position + 10 : d.position,
    }));

    // there is no currIndex ( means none of the index has selected true) all of them are false.

    //then the current index should be the last item. newWheelData.lenght

    const adjustedIndex =
      currIndex !== -1 ? currIndex : newWheelData.length - 1;

    if (automaticResizing) {
      // Gather code-block counts for *existing* segments
      let totalNodeCount = 0;
      const existingSegmentsWithCounts = updatedWheelData.map((seg) => {
        const nodeMap =
          typeof seg.nodes === "string" ? JSON.parse(seg.nodes) : seg.nodes;
        const nodeCount = countAllNodes(nodeMap, "ROOT"); // implement countAllNodes as needed
        totalNodeCount += nodeCount;
        return { ...seg, node_count: nodeCount };
      });
      void existingSegmentsWithCounts;

      // For our new (empty) segment, define at least 1 node if you want
      const newSegmentNodeCount = 1; // or more if you have default blocks
      totalNodeCount += newSegmentNodeCount;

      // Compute the fraction => new segment's value
      const ratioForNew = newSegmentNodeCount / totalNodeCount;
      const finalValue = Number((ratioForNew * 100).toFixed(2));

      // Now splice the new segment with its computed finalValue
      updatedWheelData.splice(
        adjustedIndex + 1,
        0,
        newSegment({
          id,
          currentPos,
          color,
          colorInner,
          newSegmentValue: finalValue,
        }),
      );
    } else {
      // PROPORTIONALLY REDUCE UNLOCKED SEGMENTS BEFORE INSERTING NEW ONE
      //    ---------------------------------------------------------------
      //  e.g., If the biggest unlocked segment is 50% of the total unlocked,
      //  we reduce it by ~50% of whatever space the new segment needs.
      const unlockedSum = unlockedSegments.reduce(
        (sum, sb) => sum + sb.value,
        0,
      );

      // Distribute the new segment value so that all unlocked segments
      // share the "loss" of those points.
      const newSegmentValue = Number(
        Math.floor(unlockedSum / (unlockedSegments.length + 1)).toFixed(2),
      );
      if (newSegmentValue < 1 && unlockedSegments.length > 0) return; // Don't allow sub-1 segments

      let finalValue = Math.max(1, newSegmentValue);

      // Everyone loses enough total to "give" newSegmentValue to the new segment
      const valueToReduce = finalValue;
      updatedWheelData.forEach((sb) => {
        if (!sb.resize_lock) {
          const reductionRatio = sb.value / unlockedSum;
          const reduction = Math.floor(valueToReduce * reductionRatio);
          sb.value = Number(Math.max(1, sb.value - reduction).toFixed(2));
        }
      });

      updatedWheelData.splice(
        adjustedIndex + 1,
        0,
        newSegment({
          id,
          currentPos,
          color,
          colorInner,
          newSegmentValue: finalValue,
        }),
      );
    }

    let totalData = updatedWheelData.reduce((sum, s) => sum + s.data_value, 0);

    const finalSegments = updatedWheelData.map((seg) => {
      const newDataValue = (seg.value / 100) * totalData;
      return {
        ...seg,
        data_value: Math.max(1, Math.floor(newDataValue)),
      };
    });

    dispatch(pushToHistoryAction()); // Push the new state to history

    dispatch(setWheelSegments(finalSegments));
    dispatch(handleMenuChange({ num: 2 }));
    setIsEmptyWheel(false);
    // Capture "Attach to Wheels" auto-create-Segment path (CaptureSegmentDropProvider)
    // reads this return value synchronously; every existing caller ignores it.
    return id;
  };

  useEffect(() => {
    // stgport-add-to-segment-0911: this exact editorState was already
    // written to its RIGHTFUL segment by `flushCurrentCanvasNow` (see its
    // doc comment) — by the time this effect gets a render, a Capture
    // multi-segment batch can already have switched `selectedArc` on to a
    // DIFFERENT segment, so re-running this generic write now would apply
    // segment A's content to segment B instead of being a harmless re-write.
    if (
      editorState &&
      editorState === lastFlushedEditorStateRef.current
    ) {
      return;
    }
    // batch-proof-stg-4a40541d: target the Segment `editorState` was actually
    // captured FOR (`editorStateOwnerSegmentIdRef`, set synchronously in
    // `handleNodesChange`), not `selectedArc` — see that ref's doc comment
    // above for the exact remount race that can leave `selectedArc` naming
    // the Segment being switched AWAY FROM at this point.
    const ownerSegmentId = editorStateOwnerSegmentIdRef.current ?? selectedArc?.id;
    // reproof-stg-add-to-segment-s2-after-5412: this specific `editorState`
    // came from event `nodesChangeEvent.seq` (undefined/falsy for legacy
    // callers that never went through `setNodesChangeEvent`, e.g. none in
    // this file — every path sets it — but guarded with `?? -Infinity`
    // regardless so a missing seq can never bypass this check). If a NEWER
    // event has already been flushed for this exact segment (by
    // `flushCurrentCanvasNow`, per `flushedSeqBySegmentRef`'s doc comment
    // above), this event is superseded — writing it now would revert that
    // newer, correct content back to this older one, regardless of which
    // effect happened to be scheduled or run first.
    const thisEventSeq = nodesChangeEvent?.seq ?? -Infinity;
    const flushedSeq = flushedSeqBySegmentRef.current[ownerSegmentId] ?? -Infinity;
    if (ownerSegmentId != null && flushedSeq >= thisEventSeq) {
      return;
    }
    // reproof-stg-add-to-segment-s2-after-5405: this effect is scheduled by
    // React from THIS render's commit, but it can actually RUN after a LATER,
    // unrelated dispatch has already landed in the store — most commonly
    // `flushCurrentCanvasNow` correctly flushing a DIFFERENT Segment's fresh
    // content moments earlier in the same multi-select batch. The closure
    // `wheelData` above is a snapshot from THIS render, so it does not see
    // that later write. Building `updatedWheelData` from it and dispatching
    // the WHOLE array back (every unrelated row copied through as-is)
    // silently reverts that intervening write the instant this effect fires
    // — even though `ownerSegmentId` itself is correct. `flushCurrentCanvasNow`
    // was already immune to this because it reads `store.getState()` live;
    // this fallback writer needs the same immunity for the ARRAY it rebuilds
    // from, not just for the id it targets.
    const liveWheelData = getWheelSegments(store.getState());
    const ownerSegment = liveWheelData.find((segment) => segment.id === ownerSegmentId);
    if (ownerSegment) {
      const updatedWheelData = liveWheelData.map((segment) => {
        if (segment.id === ownerSegmentId) {
          const initialNodes = segment.initialNode;

          console.log(
            // initialNodes,
            initialNodes,
            "initilNode",
            editorState,
            editorState !== initialNodes,
            initialSegmentNode,
            editorState === initialSegmentNode,
            ownerSegment,
          );
          // Only mark as modified if the nodes have actually changed from initial state OR if it doesnt have created_at (new segment)
          const isModified =
            !ownerSegment?.created_at || editorState !== initialNodes;

          return {
            ...segment,
            is_modified: isModified,
            nodes: editorState,
          };
        }
        return segment;
      });
      dispatch(pushToHistoryAction());
      dispatch(setWheelSegments(updatedWheelData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesChangeEvent]);

  // Calculate character count when segment is selected
  useEffect(() => {
    if (selectedArc) {
      try {
        const nodeMap =
          typeof selectedArc.nodes === "string"
            ? JSON.parse(selectedArc.nodes)
            : selectedArc.nodes;

        // Calculate the character count and dispatch it to Redux
        const charCount = countTextCharacters(nodeMap, "ROOT");
        dispatch(setSelectedSegmentCharCount(charCount));
      } catch (error) {
        console.error("Error counting text characters:", error);
        dispatch(setSelectedSegmentCharCount(0));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArc, editorState]);

  const handleSetChartData = (newChartData) => {
    // Create a map of segment IDs from wheelData using settings.id
    const segmentIdMap = {};
    wheelData.forEach((segment) => {
      // Use settings.id as the key to map to the segment's id
      if (segment.settings && segment.settings.id) {
        segmentIdMap[segment.settings.id] = segment.id;
      }
    });

    // Update the data while preserving original IDs by matching settings.id with segment.id
    const updatedData = newChartData.map((segment) => {
      // If this segment's ID exists in our settings.id map, use the corresponding server ID
      const originalId = segmentIdMap[segment.id];

      return {
        ...segment,
        id: originalId || segment.id, // Use original ID if available, otherwise keep the current ID
      };
    });

    console.log(newChartData, "neuv", updatedData);

    dispatch(pushToHistoryAction());
    dispatch(setWheelSegments(updatedData));
  };

  const onSegmentClose = () => {
    const idToRemove = selectedArc.id;
    const updatedChartData = clonedWheelData?.map((data) => {
      if (data?.id === idToRemove) {
        data.selected = false;
      }
      return data;
    });
    dispatch(pushToHistoryAction());

    dispatch(setWheelSegments(updatedChartData));
  };

  const clonedWheelData = wheelData.map((segment) => ({
    ...segment,
    ...segment?.settings,
  }));

  const handleNodesChange = (editorState) => {
    try {
      const json = editorState.serialize();
      editorStateRef.current = json;
      // batch-proof-stg-4a40541d: read the LIVE selection, synchronously,
      // the instant this content is captured — see
      // `editorStateOwnerSegmentIdRef`'s doc comment above for why the
      // `selectedArc` render closure the guarded effect used to trust can
      // still name the PREVIOUS Segment at this exact point in a switch.
      const ownerSegmentId = getSelectedSegment(store.getState())?.id ?? null;
      editorStateOwnerSegmentIdRef.current = ownerSegmentId;
      // reproof-stg-add-to-segment-s2-after-5412: bump BEFORE building the
      // event, so `flushCurrentCanvasNow` (which can run synchronously
      // moments later, inside the same `onNodesChange` -> `insert` ->
      // `onAfterInsert` call stack) sees the seq that corresponds to
      // EXACTLY this content.
      nodesChangeSeqRef.current += 1;
      setNodesChangeEvent({ json, seq: nodesChangeSeqRef.current, ownerSegmentId });
    } catch (error) {}
  };

  /**
   * stgport-add-to-segment-0911 (ports FE #5390/#5396/#5402/#5403's fix onto
   * staging's own, pre-autosave-refactor save path — those PRs' literal diffs
   * don't apply here, this repo's `SegmentBuilder/index.jsx` predates the
   * `structuralAutosave`/`saveResponseSequencing` machinery they're built on).
   *
   * MEASURED live on web-stg.iyuga.net (bundle main.8388ee5e.js, wheel 68,
   * segments 25/26): adding one Capture to BOTH segments in one confirm
   * landed it only in whichever segment the loop reached LAST (26), never in
   * the other (25) — 5/5 trials, positive-controlled by a single-segment add
   * that lands correctly every time.
   *
   * ROOT CAUSE: a craft insert (`actions.addNodeTree`) fires `onNodesChange`
   * synchronously, but this file only ever writes that into redux
   * (`wheelData`) via the `useEffect([editorState])` below — which needs an
   * actual React render to run. `CapturedOnWheel.handlePlace` awaits each
   * `place()` call in its multi-segment loop, but `place()` resolves
   * synchronously for a target that's already the open segment (no switch to
   * wait for) — so the loop moves on to the NEXT segment, and
   * `CaptureSegmentDropProvider.onSelectSegment` dispatches
   * `setWheelSegments` from ITS OWN `useSelector` snapshot of `wheelSegments`
   * — which is stale exactly when the render carrying the first segment's
   * insert hasn't happened yet — clobbering the just-inserted content the
   * instant the switch's dispatch lands.
   *
   * Fix: force the currently-open segment's redux row to the canvas's real,
   * CURRENT content — read via `editorStateRef` (updated synchronously in
   * `handleNodesChange` above, not gated on a render) and the live
   * `store.getState()` (not the `selectedArc`/`wheelData` closures, which can
   * be exactly as stale) — synchronously after EVERY successful insert, not
   * just before an outgoing switch. Wired as `CaptureSegmentDropProvider`'s
   * `onAfterInsert`.
   */
  const flushCurrentCanvasNow = React.useCallback(() => {
    const state = store.getState();
    const liveSelectedArc = getSelectedSegment(state);
    const liveWheelData = getWheelSegments(state);
    if (!liveSelectedArc || !Array.isArray(liveWheelData)) return;
    if (!editorStateRef.current) return;
    lastFlushedEditorStateRef.current = editorStateRef.current;
    // reproof-stg-add-to-segment-s2-after-5412: `editorStateRef.current` is
    // always the content of the LATEST `onNodesChange` event (both refs are
    // set together, synchronously, in `handleNodesChange`), so tagging this
    // flush with `nodesChangeSeqRef.current` records exactly which event's
    // content this segment now holds — see that ref's doc comment.
    flushedSeqBySegmentRef.current[liveSelectedArc.id] = nodesChangeSeqRef.current;
    const updated = liveWheelData.map((segment) =>
      segment.id === liveSelectedArc.id
        ? { ...segment, nodes: editorStateRef.current, is_modified: true }
        : segment,
    );
    dispatch(setWheelSegments(updated));
  }, [store, dispatch]);

  const handleRotation = (direction = "prev") => {
    if (!wheelData?.length) return;
    dispatch(pushToHistoryAction());
    dispatch(setWheelSegments(handleRotationData(wheelData, direction)));
  };

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isAutoSaveError, setIsAutoSaveError] = useState(false);
  const [showUnSaved, setShowUnSaved] = useState(false);

  const addIdToUrl = (id) => {
    const newPath = `${location.pathname}/${id}`;
    navigate(newPath, { replace: true });
  };

  const handleMutation = (
    mutationFunc,
    successMessage,
    payload,
    isManualSave = true,
    callback = () => {},
  ) => {
    dispatch(setIsSavingWheel(true));
    setIsAutoSaving(true);
    console.log({ payload });
    mutationFunc(payload, {
      onSuccess: (res) => {
        if (res.data.success) {
          const responseId = res?.data?.data?.id;
          if (!id) {
            addIdToUrl(responseId);
          }

          if (id && id !== responseId) {
            //id in params is not same as response id of the wheel, request by talha for cloning duplicate issue
            dispatch(setWheelId(responseId));
            window.history.replaceState(null, "", `/builder/${responseId}`);
          }

          dispatch(setIsDraft(true));
          callback && callback(res?.data?.data?.id, res?.data?.data);
          setShowUnSaved(false);
          setIsAutoSaving(false); // Stop animation after success
          if (isManualSave) {
            toast.success(successMessage); // Show toast only for manual saves
            dispatch(setIsSavingWheel(false));
          } else {
            setTimeout(() => {
              dispatch(setIsSavingWheel(false));
            }, 1000); // Delay stopping the saving wheel for auto-save
          }
          setIsAutoSaveError(false);
          prevIndexRef.current = currentIndex; // Update the previous index
        }
      },
      onError: (err) => {
        console.log({ err });
        setShowUnSaved(false);
        setIsAutoSaving(false); // Stop animation if error
        toast.error(err?.message); // Show error toast regardless
        setIsAutoSaveError(true);
        prevIndexRef.current = currentIndex; // Update the previous index

        if (!isManualSave) {
          setTimeout(() => {
            dispatch(setIsSavingWheel(false));
          }, 1000); // Delay stopping the saving wheel for auto-save errors
        } else {
          dispatch(setIsSavingWheel(false));
        }
      },
    });
  };

  console.log("hasUnsavedChanges", hasUnsavedChanges);

  // Update the handleSaveWheel function to mark changes as saved
  const handleSaveWheel = async (isManualSave = true, callback = () => {}) => {
    // Check if any segment exceeds the character limit
    let segmentWithExcessChars = null;
    for (const segment of wheelData) {
      try {
        const nodeMap =
          typeof segment.nodes === "string"
            ? JSON.parse(segment.nodes)
            : segment.nodes;
        const charCount = countTextCharacters(nodeMap, "ROOT");

        if (charCount > charLimitInASegment) {
          segmentWithExcessChars = segment;
          break;
        }
      } catch (error) {
        console.error(
          "Error counting characters for segment:",
          segment.id,
          error,
        );
      }
    }

    if (segmentWithExcessChars) {
      toast.error(
        `Unable to save: Segment ${
          segmentWithExcessChars?.settings?.title || "Untitled"
        } has exceeded it's character limit`,
        {
          style: {
            backgroundColor: "#ffffff",
            color: "#000000",
            fontWeight: "500",
            border: "1px solid #e1e1e1",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
          toastId: "segment-char-limit-error",
          autoClose: 5000,
          position: "top-center",
        },
      );
      setIsAutoSaving(false);
      dispatch(setIsSavingWheel(false));
      return;
    }

    const mutationFunction = wheelIdInRedux
      ? updateWheel.mutate
      : createWheel.mutate;
    const successMessage = wheelIdInRedux
      ? "Wheel updated successfully"
      : "Wheel created successfully";

    let payload = generatePayload(
      wheelInformation?.history[wheelInformation?.currentIndex] || {},
      wheelWallpaperSettings,
    );
    payload = wheelIdInRedux
      ? { ...payload, id: Number(wheelIdInRedux) }
      : { ...payload };

    if (!isManualSave) {
      setIsAutoSaving(true); // Start animation for auto-save
    }

    handleMutation(
      mutationFunction,
      successMessage,
      payload,
      isManualSave,
      async (id, response) => {
        // Mark changes as saved after successful save
        dispatch(markChangesSaved());

        if (response) {
          const updatedSegments = wheelData.map((segment) => {
            const matchingResponseSegment =
              response?.wheel_segments?.length > 0
                ? response?.wheel_segments?.find(
                    (respSegment) => respSegment.position === segment.position,
                  )
                : null;

            return {
              ...segment,
              id: matchingResponseSegment?.id || segment.id, // Use response ID if available
              is_modified: false,
              initialNode: segment.nodes,
            };
          });

          dispatch(setWheelSegments(updatedSegments));
        }

        // Extract media URLs and send to API
        let mediaUrls = extractMediaUrlsOnly(segments);

        // Add wheel center media URL if it's an image
        if (
          payload.wheel_center_media?.type === "image" &&
          payload.wheel_center_media?.url
        ) {
          mediaUrls.push(payload.wheel_center_media.url);
        }

        if (
          payload?.wallpaper_settings?.current_bg === 2 &&
          payload?.wallpaper_settings?.bg_wallpaper
        ) {
          mediaUrls.push(payload?.wallpaper_settings?.bg_wallpaper);
        }
        if (mediaUrls.length > 0) {
          try {
            await sendWheelMedia(Number(wheelIdInRedux), mediaUrls);
          } catch (error) {
            console.error("Error sending media URLs:", error);
            // Continue with callback even if media update fails
          }
        }

        // Check if other values changed and take screenshot if needed
        // Calculate directly here to ensure we check current state at save time
        if (
          initialWallpaperSettings !== null &&
          initialCenterMedia !== null &&
          initialSegmentsData !== null
        ) {
          const currentWheel =
            wheelInformation?.history?.[wheelInformation?.currentIndex];
          const otherValuesChanged =
            initialSegmentsData?.length !==
              currentWheel?.wheel_segments?.length ||
            !isEqual(initialCenterMedia, currentWheel?.wheel_center_media) ||
            !isEqual(
              getWallpaperComparisonProps(initialWallpaperSettings),
              getWallpaperComparisonProps(wheelWallpaperSettings),
            );

          if (otherValuesChanged) {
            await handleScreenshot();
          }
        }

        if (callback) callback(id);
      },
    );
  };

  const handleChangeSegmentRotationBox = () => {
    dispatch(setWheelRotateBoxVisibility(!rotateBoxVisibility));
  };

  const prevIndexRef = useRef(wheelInformation?.currentIndex); // Store the previous index at the top level
  useEffect(() => {
    if (!id) return; // Auto-save only for edit mode

    const interval = setInterval(() => {
      const currentIndex = wheelInformation?.currentIndex;

      // Check if the index has changed
      if (prevIndexRef.current !== currentIndex) {
        // Check if any segment exceeds limit before auto-saving
        let hasExcessChars = false;
        for (const segment of wheelData) {
          try {
            const nodeMap =
              typeof segment.nodes === "string"
                ? JSON.parse(segment.nodes)
                : segment.nodes;
            const charCount = countTextCharacters(nodeMap, "ROOT");

            if (charCount > charLimitInASegment) {
              hasExcessChars = true;
              break;
            }
          } catch (error) {
            console.error("Error checking segment chars for auto-save:", error);
          }
        }

        if (!hasExcessChars) {
          handleSaveWheel(false); // Trigger auto-save only if within limits
          prevIndexRef.current = currentIndex; // Update the previous index
        }
      }
    }, 30000); // Save every 30 seconds

    return () => clearInterval(interval); // Cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, wheelInformation, wheelWallpaperSettings, wheelData]);

  const [undoRedoTrigger, setUndoRedoTrigger] = useState(0);
  void setUndoRedoTrigger; // used as key in Grid item

  const onClickPortrait = () => {
    dispatch(handleMenuChange({ num: 1 }));
    dispatch(setWheelSettingsTab("1"));
    dispatch(setDesignTabTarget("portrait"));
  };

  useEffect(() => {
    if (!id) {
      handleSaveWheel(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Make sure the effect runs whenever 'id' changes

  const currentIndex = wheelInformation?.currentIndex;
  useEffect(() => {
    if (prevIndexRef.current !== currentIndex) {
      setShowUnSaved(true); // Set `showUnSaved` to true if `currentIndex` changes
    }
  }, [currentIndex]);

  const [showBroadcastModal, setshowBroadcastModal] = useState(false);
  const toggleBroadcastModal = () => {
    setshowBroadcastModal(!showBroadcastModal);
  };

  const {
    mutate: sendBroadCast,
    isLoading,
    isError,
    error,
  } = useBroadcastChanges();

  const onSendBroadcastPress = () => {
    sendBroadCast(wheelIdInRedux, {
      onSuccess: () => {
        setshowBroadcastModal(false);
        setshowUpdateCloneModal(false);
        dispatch(setIsDraft(false));
        toast.success("Clone Synchronization Successful!");
        // Check if onLeave is a function before calling it
        if (typeof onLeave === "function") {
          onLeave();
        }
      },
    });
  };

  React.useEffect(() => {
    if (isError) {
      toast.error(`Error occurred: ${error?.message || "Unknown error"}`);
    }
  }, [isError, error]);

  const setLeaveModalOpen = (val) => {
    setModalOpen(val);
  };

  const isLive = useSelector(getWheelIsLiveValue);
  const isDraft = useSelector(getWheelIsDraftValue);

  // Update the handleLogoClick function to use the hasUnsavedChanges state
  const handleLogoClick = () => {
    if (isLive && isDraft) {
      setOnLeave(() => () => nav(`/gallery/${getLoggedInUser().id}`));
      setshowUpdateCloneModal(true);
      return;
    }

    if (hasUnsavedChanges) {
      setOnLeave(() => () => nav(`/gallery/${getLoggedInUser().id}`));
      setModalOpen(true);
    } else {
      getLoggedInUser()
        ? nav(`/gallery/${getLoggedInUser().id}`)
        : nav("/login");
    }
  };

  const onSaveAndExitPress = () => {
    handleSaveWheel(true);
    onLeave();
  };

  // Screenshot functionality moved to backend Lambda

  const handleScreenshot = async () => {
    try {
      console.log("Calling backend Lambda to take wheel screenshot");
      await takeWheelScreenshot(wheelIdInRedux);
      console.log("Screenshot taken successfully by backend Lambda");

      // Refresh wheel data to get updated preview
      if (wheelIdInRedux) {
        const wheelDetails = await getWheelDetails(wheelIdInRedux);
        if (wheelDetails?.data?.data?.wheel_preview) {
          dispatch(setWheelPreview(wheelDetails.data.data.wheel_preview));
        }
      }
    } catch (error) {
      console.error("Error taking wheel screenshot:", error);
    }
  };

  const handleAddSegmentAfterAllLocked = () => {
    dispatch(handleMenuChange({ num: 1 })); // Open design tab
    dispatch(setDesignTabTarget("segments"));
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Just dispatch close modal action
      // We don't need to prevent default here as we want the navigation to proceed
      dispatch(closeModal());
    };

    const handlePopState = () => {
      dispatch(closeModal());
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Clean up
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch]);

  // Check if any segment exceeds the character limit for UI feedback
  const hasCharLimitExceeded = wheelData.some((segment) => {
    try {
      const nodeMap =
        typeof segment.nodes === "string"
          ? JSON.parse(segment.nodes)
          : segment.nodes;
      const charCount = countTextCharacters(nodeMap, "ROOT");
      return charCount > charLimitInASegment;
    } catch (error) {
      return false;
    }
  });

  // fix-capture-stg-builder-390-squeezed-desktop-v3: below the shared 768
  // mobile floor, show the SAME desktop-only interstitial Gallery/View-Wheel
  // already use instead of the broken desktop canvas. All hooks above run
  // unconditionally, so this early return respects the Rules of Hooks.
  if (isBuilderGated) {
    return (
      <MobileModal
        open
        disableClose
        handleClose={() => navigate(id ? `/wheel/${id}` : "/gallery")}
        wheelData={wheelData}
        titleOverride="Wheel building is desktop-only"
        descriptionOverride="Building your Wheels isn't currently possible on mobile devices — visit web.iyuga.net on your computer to build or edit."
      />
    );
  }

  return (
    <>
      <Editor onNodesChange={handleNodesChange}>
        {/* Capture / Notepad inbox (WL-021) — drop-to-Segment wiring. Self-gates
            on CAPTURE_ENABLED + CAPTURE_SEGMENT_DROP_ENABLED (both default OFF),
            so this renders exactly as before until both flags are flipped. Must
            be inside <Editor>: useInsertCaptureIntoSegment calls craft's
            useEditor(). */}
        <CaptureSegmentDropProvider
          onCreateSegment={addSegment}
          onAfterInsert={flushCurrentCanvasNow}
        >
        {/* <button onClick={handleScreenshot}>Take Wheel Screenshot</button> */}
        <WheelLayout
          handleSaveWheel={handleSaveWheel}
          setModalOpen={setLeaveModalOpen}
          setOnLeave={setOnLeave}
          onClickBeta={() => setWaitListModalVisible(true)}
          isAutoSaving={isAutoSaving}
          setIsAutoSaving={setIsAutoSaving}
          isAutoSaveError={isAutoSaveError}
          unsavedChanges={prevIndexRef.current !== currentIndex && showUnSaved}
          onBroadCastPress={toggleBroadcastModal}
          handleLogoClick={handleLogoClick}
          isEditScreen={true}
          hasCharLimitExceeded={hasCharLimitExceeded}
        >
          <BroadcastModal
            // prefixIcon={null}
            title="Synchronize Wheel Update!"
            modalText="Update all Synced Clones of this Wheel to the current version?"
            cancelButtonVisible={true}
            handleClose={toggleBroadcastModal}
            isOpen={showBroadcastModal}
            buttonText="Synchronize"
            onSubmit={onSendBroadcastPress}
            buttonLoading={isLoading}
          ></BroadcastModal>
          {/* <div>
            <button onClick={handleUndo}>Undo</button>
            <button onClick={handleRedo}>Redo</button>
          </div> */}
          {/* <UndoRedoComponent /> */}
          <Styled.MainDiv isEmptyWheel={isEmptyWheel}>
            <Styled.FlexMainDiv>
              <animated.div
                style={{
                  ...firstDivStyles,
                  height: "92vh",
                  position: "relative",
                  zIndex: 0,
                  overflow: "hidden",
                }}
              >
                {(currentBg === 2 || currentBg === 3) &&
                  base64Wallpaper !== "" && (
                  <Styled.WheelWallpaperImage
                    currentBg={currentBg}
                    bgWallpaper={base64Wallpaper}
                    wallpaperTransparency={wallpaperTransparency}
                    wallpaperHuePercentage={wallpaperHuePercentage}
                 
                  >
                    {currentBg === 2 && wallpaperHuePercentage > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: wallpaperHueColor,
                          mixBlendMode: "multiply",
                          opacity:
                            ((wallpaperHuePercentage ?? 0) / 100 ),
                        }}
                      />
                    )}
                  </Styled.WheelWallpaperImage>
                )}
                {currentBg === 0 && (
                  <Styled.WheelWallpaperColor
                    color={resolvedBgColor}
                    wallpaperTransparency={wallpaperTransparency}
                  />
                )}

                {loading ? (
                  <Loader />
                ) : (
                  <Styled.LeftSection>
                    <Grid
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        marginLeft: !!selectedMenu ? "0" : "min(6vw, 60px)",
                        transition: "margin-left 0.3s ease",
                        padding: "0 20px",
                      }}
                    >
                      <WheelComponent
                        width={isUltraWide ? "min(35vw, 700px)" : "min(35vw, 500px)"}
                        chartData={clonedWheelData}
                        setChartData={handleSetChartData}
                        showLabels={showLabels}
                        resize={false}
                        config={{
                          isNotDraggable: false,
                          segmentHighlighting: false,
                          // segmentLableOpacity: 1,
                        }}
                        userData={{
                          id: uuidv4(),
                          url:
                            wheelInformation?.history[
                              wheelInformation?.currentIndex
                            ]?.wheel_center_media?.url ||
                            ASSETS.userPlaceholder,
                        }}
                        onClickPortrait={onClickPortrait}
                      />

                      <SegmentActions
                        value={showLabels}
                        handleSwitch={handleSwitch}
                        handleSegmentButton={addSegment}
                        handleRotation={handleRotation}
                        selectedArc={selectedArc}
                        isSideMenuActive={isSideMenuActive}
                        isOpen={rotateBoxVisibility}
                        handleClick={handleChangeSegmentRotationBox}
                        isShowLeftOrRight={
                          wheelData?.length !== 0 || !isEmptyWheel
                        }
                        wheelData={wheelData}
                      />
                    </Grid>
                    {selectedArc ? (
                      <Grid item key={undoRedoTrigger}>
                        {/* Capture / Notepad inbox — the open Segment's canvas
                            AS a drop target for a capture dragged out of the
                            Captured drawer. Flag OFF: renders children verbatim,
                            no wrapper element, no layout change. */}
                        <BuilderCaptureDropZone>
                          <SegmentView
                            selectedArc={selectedArc}
                            handleChange={handleChange}
                            handleMenuChange={(val) =>
                              dispatch(
                                handleMenuChange({ num: val, selectedArc }),
                              )
                            }
                            onClose={onSegmentClose}
                            isBuilder={true}
                          />
                        </BuilderCaptureDropZone>
                      </Grid>
                    ) : null}
                  </Styled.LeftSection>
                )}
              </animated.div>
              <animated.div
                style={{
                  ...secondDivStyles,
                  height: "92vh",
                  overflowY: "auto",
                  overflowX: "hidden",
                  backgroundColor: "white",
                  flexShrink: 0,
                }}
              >
                {componentMap.map(({ isVisible, Component, props }, index) => (
                  <div
                    key={index}
                    style={{ display: isVisible ? "block" : "none" }} // Maintain mounting
                  >
                    {isVisible ? <Component {...props} /> : null}
                  </div>
                ))}
              </animated.div>
            </Styled.FlexMainDiv>
          </Styled.MainDiv>
          <DeleteModal />
          <MoveBlockModal wheelData={wheelData} />
        </WheelLayout>
        </CaptureSegmentDropProvider>
      </Editor>

      <WaitlistFormModal
        open={waitListModalVisible}
        onClose={() => setWaitListModalVisible(false)}
      />

      <SaveChangesModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onLeave={onLeave}
        onSave={() => {
          handleSaveWheel(true);
          onLeave();
        }}
        title={"Save Changes"}
        subtitle={
          "You have some unsaved changes. Are you sure you want to proceed without saving?"
        }
      />

      <UpdateLiveClones
        // prefixIcon={null}

        title="Update Sync Clones?"
        modalText="You have not synchronized the changes you've made to the Sync Clones of this Wheel. What would you like to do?"
        cancelButtonVisible={true}
        prefixIcon={
          <SyncProblemIcon style={{ color: "#E87033", marginRight: 5 }} />
        }
        handleClose={() => setshowUpdateCloneModal(false)}
        isOpen={showUpdateCloneModal}
        buttonText="Synchronize"
        repeatButtonText={"Save & Exit"}
        onBroadCastPress={onSendBroadcastPress}
        onSaveExitPress={onSaveAndExitPress}
        onExitPress={onLeave}
        buttonLoading={isLoading}
      ></UpdateLiveClones>

      <GallerySettings
        open={gallerySettingsOpen}
        onClose={() => {
          setGallerySettingsOpen(false);
          setOpenLibraryInBusinessBox(false);
        }}
        openDefault={!isEmpty(bboxData)}
        hideTabs
        title="Business Box"
        isWheelBusinessBox
        openLibrarySection={openLibraryInBusinessBox}
      />

      <ConfirmationModal
        isOpen={confirmationModalOpen}
        handleClose={() => setConfirmationModalOpen(false)}
        onConfirm={() => {
          handleBusinessBoxEditorOpen();
        }}
        title="Edit Alert"
        message={
          "Editing this Business Box will update it wherever it's used. Are you sure you wish to proceed?"
        }
      />

      <ConfirmationModal
        isOpen={lockWarningModalOpen}
        handleClose={() => setLockWarningModalOpen(false)}
        onConfirm={handleAddSegmentAfterAllLocked}
        title="Can't Add a Segment"
        message="All segments on this wheel are size-locked, so there's no space for a new one. To add a segment, unlock one or more existing segments."
        confirmText="Proceed"
      />

      {selectedType === "images" && isPreviewOpen && (
        <PreviewCarousel
          imageArray={
            segments?.filter((e) => e?.title === selectedSegment)[0]?.items
              ?.images
          }
          selectedImage={selectedItem}
        />
      )}

      {/* Screenshot functionality moved to backend Lambda */}
    </>
  );
}
